import React, {useState,useEffect, useMemo, useCallback, useRef} from 'react';
import MaterialTable from 'material-table';
import { forwardRef } from 'react';

import AddBox from '@material-ui/icons/AddBox';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';
import Visibility from '@material-ui/icons/Visibility';
import { Box, Tooltip, Typography, Modal, Paper, Button, TextField, IconButton } from '@material-ui/core';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { CircularProgress } from '@material-ui/core';
import apiClient from '../apiClient';

const tableIcons = {
Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />)
};

// Simple Date Picker Component for pickup dates
const DatePickerField = React.memo(({ value, onChange, rowData }) => {
  const dateValue = value ? new Date(value) : null;

  const handleDateChange = (newDate) => {
    if (newDate && !isNaN(newDate.getTime())) {
      const formattedDate = newDate.toISOString().split('T')[0]; // YYYY-MM-DD format
      onChange(formattedDate, rowData);
    }
  };

  const handleClick = (e) => {
    e.stopPropagation(); // Prevent row click
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        value={dateValue}
        onChange={handleDateChange}
        onClick={handleClick}
        slotProps={{
          textField: {
            size: 'small',
            variant: 'outlined',
            style: { width: '140px' }
          }
        }}
      />
    </LocalizationProvider>
  );
});

// Highly optimized Notes Input Component using uncontrolled input to prevent re-renders
const NotesInput = React.memo(({ value, onChange, placeholder, style, InputProps }) => {
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);
  const lastValueRef = useRef(value);

  // Set initial value when component mounts or value prop changes
  useEffect(() => {
    if (inputRef.current && inputRef.current.value !== value) {
      inputRef.current.value = value || '';
      lastValueRef.current = value || '';
    }
  }, [value]);

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    lastValueRef.current = newValue;
    
    // Debounce the onChange callback to parent
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      onChange(newValue);
    }, 500); // Increased to 500ms for even better performance
  }, [onChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <TextField
      inputRef={inputRef}
      multiline
      rows={4}
      fullWidth
      variant="outlined"
      defaultValue={value}
      onChange={handleChange}
      placeholder={placeholder}
      style={style}
      InputProps={InputProps}
    />
  );
});

// Move static definitions outside component to avoid initialization issues
const cellStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '200px',
  height: '50px',
};

const flagData = {
  allParts: { color: 'lightgreen', name: 'All Parts Here' },
  readyPaint: { color: 'orange', name: 'Ready for Paint' },
  allRepairNoParts: { color: 'blue', name: 'All Repairs - no parts' },
  hospital: { color: 'red', name: 'HOSPITAL' },
  requireFrame: { color: 'black', name: 'Requires Frame' },
  requireMech: { color: 'pink', name: 'Requires Mechanical' },
  requireGlass: { color: 'grey', name: 'Requires Glass' },
  needsCalibration: { color: 'purple', name: 'Needs Calibration' },
  totalLoss: { color: 'brown', name: 'Total Loss' },
};

const darkColors = ['black', 'blue', 'red', 'purple'];

const getTextColor = (backgroundColor) => {
  return darkColors.includes(backgroundColor) ? '#fff' : '#000';
};

const stageColorMapping = {
  "Repair Planning": "#FAE9B0",
  "Waiting to Start": "#F1AE6A",
  "Frame": "#CB4C4F",
  "Mechanical": "#B66C80",
  "Body": "#895B96",
  "Refinish": "#709FC1",
  "Assembly": "#90B270",
  "Detail": "#5F8356",
  "Ready for Delivery": "#202424",
};

const getRowStyle = (rowData) => {
  const stageName = rowData.stage_detail && rowData.stage_detail.name;
  const backgroundColor = stageColorMapping[stageName] || "#FFF";
  const color = ["#FAE9B0", "#F1AE6A"].includes(backgroundColor) ? "#000000" : "#ffffff";
  return { backgroundColor, color };
};

  const DataTable = ({ data, onDataSelect, filter, stageFilter, onDataRefresh, onDataUpdate }) => {
    const [loading, setLoading] = useState(true);
    const [notesDialogOpen, setNotesDialogOpen] = useState(false);
    const [selectedNotes, setSelectedNotes] = useState('');
    const [editingNotes, setEditingNotes] = useState(false);
    const [editedNotes, setEditedNotes] = useState('');
    const [currentRowData, setCurrentRowData] = useState(null);
    const [uid] = useState(Math.random().toString(36).substring(2) + Date.now().toString(36));
  
    useEffect(() => {
      if (data && data.length > 0) {
        setLoading(false);
      }
    }, [data]);

    // Define all handlers before useMemo hooks to avoid initialization issues
    const handleNotesClick = (notes, rowData) => {
      setSelectedNotes(notes || '');
      setEditedNotes(notes || '');
      setCurrentRowData(rowData);
      setNotesDialogOpen(true);
      setEditingNotes(false);
    };

    const handleNotesClose = (event) => {
      if (event) event.stopPropagation();
      setNotesDialogOpen(false);
      setSelectedNotes('');
      setEditedNotes('');
      setCurrentRowData(null);
      setEditingNotes(false);
    };

    const handleStartEdit = (event) => {
      if (event) event.stopPropagation();
      setEditingNotes(true);
    };

    const handleCancelEdit = (event) => {
      if (event) event.stopPropagation();
      setEditedNotes(selectedNotes);
      setEditingNotes(false);
    };

    const handleSaveNotes = async (event) => {
      console.log('Save button clicked - preventing default behavior');
      event.stopPropagation(); // Prevent event bubbling to table
      event.preventDefault(); // Prevent default behavior
      
      try {
        console.log('Saving notes for RO:', currentRowData.ro, 'Notes:', editedNotes);
        
        const response = await apiClient.patch(`/repairOrders/${currentRowData.id}/`, {
          notes: editedNotes,
          session: uid,
          changeType: 'notes'
        });
        
        console.log('API response received:', response.status);
        
        if (response.status === 200) {
          console.log('Notes saved successfully');
          setSelectedNotes(editedNotes);
          setEditingNotes(false);
          
          // Close the modal immediately to prevent event issues
          setNotesDialogOpen(false);
          
          // Update local data to reflect the notes change
          if (onDataUpdate) {
            console.log('Updating local data with new notes');
            onDataUpdate(currentRowData.id, { notes: editedNotes });
          }
          
          // Show success feedback
        } else {
          console.error('Failed to save notes, status:', response.status);
          alert('Failed to save notes. Please try again.');
        }
      } catch (error) {
        console.error('Error saving notes:', error);
        alert('Error saving notes. Please check your connection and try again.');
      }
    };

    const handleUpdatePickupDate = async (newValue, rowData) => {
      try {
        const response = await apiClient.patch(`/repairOrders/${rowData.id}/`, {
          pickup_date: newValue,
          session: uid,
          changeType: 'pickup_date'
        });
        
        if (response.status === 200) {
          // Note: Removed onDataSelect() call to prevent unnecessary websocket triggers
          // The data will be updated through websocket notifications from the server
          return { ...rowData, pickup_date: newValue };
        }
      } catch (error) {
        console.error('Error updating pickup date:', error);
        throw error;
      }
    };

    const handleCellClick = (event, rowData, column) => {
      if (column && (column.field === 'notes' || column.field === 'pickup_date')) {
        event.stopPropagation();
      }
    };

    const handlePickupDateUpdate = async (newData, oldData) => {
      try {
        // Update the pickup date via API
        const response = await apiClient.patch(`/repairOrders/${newData.id}/`, {
          pickup_date: newData.pickup_date,
          session: uid,
          changeType: 'pickup_date'
        });
        
        if (response.status === 200) {
          // Update was successful
          return Promise.resolve();
        } else {
          return Promise.reject('Failed to update pickup date');
        }
      } catch (error) {
        console.error('Error updating pickup date:', error);
        return Promise.reject(error);
      }
    };

    // Memoize the notes input handler to prevent re-renders
    const handleNotesChange = useCallback((newValue) => {
      setEditedNotes(newValue);
    }, []);

    // Memoize table columns to prevent re-renders
  const tableColumns = useMemo(() => [
    { 
      title: '', 
      field: 'actions', 
      sorting: false, 
      searchable: false,
      width: 60,
      cellStyle: { paddingLeft: '8px', paddingRight: '8px' },
      render: rowData => (
        <Tooltip title="View Repair Order Details">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/#/repairorder/${rowData.id}/show/`, '_blank');
            }}
            style={{ color: '#1976d2' }}
          >
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    },
    { title: 'RO#', field: 'ro', sortable: true },
    { title: 'Vehicle In Date', field: 'vehicleInDate', sortable: true, type: 'date',cellStyle: cellStyle },
    { title: 'Vehicle Out Date', field: 'vehicleOutDate', sortable: true, type: 'date',cellStyle: cellStyle },
    { title: 'Pick Up Date', field: 'pickup_date', sortable: true, cellStyle: cellStyle,
      render: rowData => (
        <DatePickerField 
          value={rowData.pickup_date}
          onChange={handleUpdatePickupDate}
          rowData={rowData}
        />
      )
    },
    { title: 'Start Date', field: 'startInDate', sortable: true, type: 'date',cellStyle: cellStyle },
    { title: 'Total Hours', field: 'totalHours', sortable: true, type: 'numeric' },
    { title: 'Price', field: 'priceInCents', sortable: true,type: 'numeric', render: rowData => rowData.priceInCents ? `$${(rowData.priceInCents / 100).toFixed(2)}` : '-'},
    { title: 'Stage', field: 'stage_detail.name', sortable: true,cellStyle: cellStyle },
    { 
      title: 'Notes', 
      field: 'notes', 
      sortable: true, 
      cellStyle: { ...cellStyle, cursor: 'pointer', maxHeight: '40px', lineHeight: '20px' },
      render: rowData => (
        <div 
          className="notes-cell"
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click
            handleNotesClick(rowData.notes, rowData);
          }}
          style={{ 
            maxHeight: '40px', 
            overflow: 'hidden', 
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          {rowData.notes ? (
            <span>{rowData.notes.length > 100 ? `${rowData.notes.substring(0, 100)}...` : rowData.notes}</span>
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>Click to add notes</span>
          )}
        </div>
      )
    },
    { 
      title: 'Flags', 
      field: 'flags', 
      sortable: true, 
      cellStyle: cellStyle, 
      render: rowData => (
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {Object.entries(flagData).map(([flag, data]) => (
            rowData[flag] ? (

                  <Typography style={{ color: 'black', fontSize: '0.75rem' }}>
                    {data.name}
                  </Typography>

            ) : null
          ))}
        </div>
      )
    },
    // { title: 'Shop', field: 'shop.name', sortable: true },
    { title: 'Insurance Company', field: 'insuranceCompany', sortable: true,cellStyle: cellStyle },
    { title: 'VIN', field: 'vin', sortable: true },
    { title: 'Vehicle Year', field: 'vehicleYear', sortable: true, type: 'numeric' },
    { title: 'Vehicle Make', field: 'vehicleMake', sortable: true },
    { title: 'Vehicle Model', field: 'vehicleModel', sortable: true, cellStyle: cellStyle },
    { title: 'Vehicle Body Style', field: 'vehicleBodyStyle', sortable: true,cellStyle: cellStyle },
    { title: 'Vehicle Trim Package', field: 'vehicleTrimPackage', sortable: true },
    { title: 'Vehicle Color', field: 'vehicleColor', sortable: true,cellStyle: cellStyle },
    { title: 'Vehicle Interior Color', field: 'vehicleInteriorColor', sortable: true },
    { title: 'Vehicle Paint Code', field: 'vehiclePaintCode', sortable: true },
    { title: 'Vehicle Trim Code', field: 'vehicleTrimCode', sortable: true },
    { title: 'Vehicle Production Date', field: 'vehicleProductionDate', sortable: true, type: 'date' },
    { title: 'Vehicle Mileage In', field: 'vehicleMileageIn', sortable: true, type: 'numeric' },
    { title: 'Customer Name', field: 'customerName', sortable: true,cellStyle: cellStyle },
    { title: 'Estimator Name', field: 'estimatorName', sortable: true,cellStyle: cellStyle },
    { title: 'Estimator ID', field: 'estimatorId', sortable: true },
    { title: 'All Parts', field: 'allParts', sortable: true, type: 'boolean' },
    { title: 'Total Loss', field: 'totalLoss', sortable: true, type: 'boolean' },
    { title: 'Ready Paint', field: 'readyPaint', sortable: true, type: 'boolean' },
    { title: 'All Repair No Parts', field: 'allRepairNoParts', sortable: true, type: 'boolean' },
    { title: 'Hospital', field: 'hospital', sortable: true, type: 'boolean' },
    { title: 'Require Frame', field: 'requireFrame', sortable: true, type: 'boolean' },
    { title: 'Require Mech', field: 'requireMech', sortable: true, type: 'boolean' },
    { title: 'Require Glass', field: 'requireGlass', sortable: true, type: 'boolean' },
    {title: 'Needs Calibration', field: 'needsCalibration', sortable: true, type: 'boolean' },
  ], [handleNotesClick, handleUpdatePickupDate, cellStyle, flagData]);

  // Memoize table options
  const tableOptions = useMemo(() => ({
    sorting: true,
    pageSize: 20,
    pageSizeOptions: [10, 20, 50],
    rowStyle: rowData => getRowStyle(rowData),
    actionsColumnIndex: -1,
  }), [getRowStyle]);

  const filteredData = useMemo(() => {
    let result = data;

    if (filter) {
      result = result.filter(item => item[filter] !== null && item[filter] !== undefined);
    }

    if (stageFilter) {
      result = result.filter(item => item.stage_detail.name === stageFilter);
    }

    return result;
  }, [data, filter, stageFilter]);

  return (
    <>
    {loading ? (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    ) : (
    <>
    <MaterialTable
    icons={tableIcons}
      title="Repair Orders"
      columns={tableColumns}
      data={filteredData}  // Pass the filtered data here
      options={tableOptions}
      onRowClick={(event, rowData) => {
        // Check if the click target is within a notes cell, date picker, or any interactive element
        if (event.target.closest('.notes-cell') || 
            event.target.closest('.MuiDatePicker-root') ||
            event.target.closest('.MuiTextField-root') ||
            event.target.closest('.MuiInputBase-root') ||
            event.target.closest('.MuiIconButton-root') ||
            event.target.closest('[role="button"]') ||
            // Check for date picker popup elements (rendered in portals)
            event.target.closest('.MuiDateCalendar-root') ||
            event.target.closest('.MuiPickersDay-root') ||
            event.target.closest('.MuiPickersCalendarHeader-root') ||
            event.target.closest('.MuiDateCalendar-viewTransitionContainer') ||
            event.target.closest('.MuiPickersPopper-root') ||
            event.target.closest('[role="dialog"]') ||
            event.target.closest('.MuiModal-root') ||
            event.target.closest('.MuiPaper-root') ||
            notesDialogOpen || // Prevent row clicks when modal is open
            document.querySelector('.MuiDateCalendar-root:hover')) {
          return; // Don't navigate if clicking on interactive elements
        }
        onDataSelect(rowData.id);
      }}
    />
    
    {/* Notes Modal using Material-UI Modal instead of Dialog */}
    {notesDialogOpen && (
      <Modal
        open={notesDialogOpen}
        onClose={handleNotesClose}
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <Paper
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          style={{
            backgroundColor: 'white',
            border: '2px solid #000',
            boxShadow: 24,
            padding: '20px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            outline: 'none'
          }}
        >
          {/* Modal Header */}
          <Typography variant="h6" component="h2" style={{ marginBottom: '16px' }}>
            {editingNotes ? 'Edit Notes' : 'Notes'}
            {currentRowData && ` - RO #${currentRowData.ro}`}
          </Typography>
          
          {/* Modal Content */}
          <div>
            {editingNotes ? (
              <NotesInput
                value={editedNotes}
                onChange={handleNotesChange}
                placeholder="Enter notes here..."
                style={{ marginTop: '16px' }}
                InputProps={{
                  style: { fontSize: '14px' }
                }}
              />
            ) : (
              <Typography variant="body1" style={{ whiteSpace: 'pre-wrap', padding: '16px', minHeight: '100px' }}>
                {selectedNotes || 'No notes available. Click Edit to add notes.'}
              </Typography>
            )}
          </div>
          
          {/* Modal Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
            {editingNotes ? (
              <>
                <Button 
                  type="button"
                  onClick={handleCancelEdit} 
                  color="secondary"
                >
                  Cancel
                </Button>
                <Button 
                  type="button"
                  onClick={handleSaveNotes} 
                  color="primary" 
                  variant="contained"
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button 
                  type="button"
                  onClick={handleStartEdit} 
                  color="primary" 
                  variant="contained"
                >
                  Edit
                </Button>
                <Button 
                  type="button"
                  onClick={handleNotesClose} 
                  color="secondary"
                >
                  Close
                </Button>
              </>
            )}
          </div>
        </Paper>
      </Modal>
    )}
    </>
)}</>
  );
};

export default DataTable;
